import { request } from 'app/utils';
import { useSchool } from 'features/school';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  clearCommentsAction,
  getComments,
  setCommentConfigAction,
  setCommentsAction,
  setCommentTypeFilterAction,
  setReplyingCommentUuidAction,
  setReportingCommentUuidAction,
} from './redux/slice';
import { CommentsConfig, CommentsReactionEnum, CommentsReportEnum, CommentsResponse, CommentsTypeEnum, SendCommentsProps } from './types';

const useComments = () => {
  const dispatch = useDispatch();

  const { productSlug, productClassUuid, contentUuid } = useParams();

  const { comments, replyingCommentUuid, reportingCommentUuid, commentTypeFilter, commentsConfig, fetchedComments } = useSelector(getComments);

  const { apiGatewayUrl } = useSchool();

  const setComments = (comments: CommentsResponse) => {
    dispatch(setCommentsAction(comments));
  };

  const clearComments = () => {
    dispatch(clearCommentsAction());
  };

  const setReplyingCommentUuid = (commentUuid: string) => {
    dispatch(setReplyingCommentUuidAction(commentUuid));
  };

  const setReportingCommentUuid = (commentUuid: string) => {
    dispatch(setReportingCommentUuidAction(commentUuid));
  };

  const setCommentTypeFilter = (commentTypeFilter: CommentsTypeEnum | null) => {
    dispatch(setCommentTypeFilterAction(commentTypeFilter));
  };

  const setCommentConfig = (commentConfig: CommentsConfig) => {
    dispatch(setCommentConfigAction(commentConfig));
  };

  const fetchComments = useCallback(
    (cb?: () => void) => {
      request<CommentsResponse>({
        url: `/api/v1/School/ProductContentComment/${productSlug}/${productClassUuid}/${contentUuid}`,
        method: 'get',
        baseURL: apiGatewayUrl,
        params: {
          commentType: commentTypeFilter || undefined,
        },
      })
        .then(({ data }) => {
          setComments(data);
        })
        .finally(() => {
          if (cb) cb();
        });
    },
    [productSlug, productClassUuid, contentUuid, commentTypeFilter]
  );

  const createComment = useCallback(
    async (comment: SendCommentsProps, commentUuid?: string) => {
      return request({
        url: commentUuid
          ? `/api/v1/School/ProductContentComment/${productSlug}/${productClassUuid}/${contentUuid}/${commentUuid}/answer`
          : `/api/v1/School/ProductContentComment/${productSlug}/${productClassUuid}/${contentUuid}`,
        method: 'post',
        baseURL: apiGatewayUrl,
        data: {
          text: comment.text,
          commentType: commentUuid ? CommentsTypeEnum.Answer : comment.commentType,
          publicPersonaUuid: comment.publicPersonaUuid || null,
        },
      }).then(({ data }) => {
        fetchComments();
      });
    },
    [productSlug, productClassUuid, contentUuid]
  );

  const sendReaction = useCallback(
    (commentUuid: string, reaction: CommentsReactionEnum) => {
      request({
        url: `/api/v1/School/ProductContentComment/${productSlug}/${productClassUuid}/${contentUuid}/${commentUuid}/react`,
        method: 'patch',
        baseURL: apiGatewayUrl,
        params: {
          reaction,
        },
      }).then(({ data }) => {
        fetchComments();
      });
    },
    [productSlug, productClassUuid, contentUuid]
  );

  const fetchCommentsConfig = useCallback(() => {
    request<CommentsConfig>({
      url: `/api/v1/School/ProductContentComment/${productSlug}/${productClassUuid}/${contentUuid}/isEnabled`,
      method: 'get',
      baseURL: apiGatewayUrl,
    }).then(({ data }) => {
      setCommentConfig(data);
    });
  }, [productSlug, productClassUuid, contentUuid]);

  const sendReport = useCallback(
    async (commentUuid: string, reportReason: CommentsReportEnum, cb: () => void) => {
      await request({
        url: `/api/v1/School/ProductContentComment/${productSlug}/${productClassUuid}/${contentUuid}/${commentUuid}/report`,
        method: 'patch',
        baseURL: apiGatewayUrl,
        params: {
          reportReason,
        },
      }).then(() => {
        fetchComments();

        if (cb) cb();
      });
    },
    [productSlug, productClassUuid, contentUuid]
  );

  return {
    comments,
    setComments,
    clearComments,
    fetchedComments,
    fetchComments,
    createComment,

    replyingCommentUuid,
    setReplyingCommentUuid,

    reportingCommentUuid,
    setReportingCommentUuid,

    sendReaction,
    sendReport,

    commentsConfig,
    setCommentConfig,
    fetchCommentsConfig,

    commentTypeFilter,
    setCommentTypeFilter,
  };
};

export default useComments;
